import React, { useCallback, useEffect, useState } from "react"
import { Button, CircularProgress } from "@material-ui/core"
import Grid from "@material-ui/core/Grid"
import clsx from "clsx"
import { Toast } from "components/shared/Toaster"
import InputFieldDetailed from "components/forms/Input"
import { PasswordValidator } from "components/PasswordValidation/PasswordValidator"
import strings from "strings"
import whiteLogo from "styles/images/icons/whiteLogo.svg"
import darkLogo from "styles/images/logo.svg"
import Auth from 'tools/AuthService'
import request from "tools/request"
import "./LoginPage.css"
import TypoGraph from "../forms/typograph";
import SelfRegister from "./SelfRegister/SelfRegister";
import RegisterOtp from "./RegisterOtp/RegisterOtp";
import OtpCodeInput from "./OtpCodeInput/OtpCodeInput";
import ConfirmationEmailPage from "../ConfirmationEmail/ConfirmationEmailPage";
import cbrtCapital from "styles/images/clients/cbrtCapital.png"
import singlePoint from "styles/images/clients/singlePoint.png"
import claimFunded from "styles/images/clients/claimFunded.png"
import trilogy from "styles/images/clients/trilogy.png"
import xlerateFinancial from "styles/images/clients/xlerateFinancial.png"
import capitalHart from "styles/images/clients/capitalHart.png"
import boltonCapital from "styles/images/clients/boltonCapital.png"
import r7 from "styles/images/clients/r7.png"
import aspenInvoiceFactoring from "styles/images/clients/aspenInvoiceFactoring.png"
import jobe from "styles/images/clients/jobe.png"
import { TRIAL_INCLUDED_CREDITS } from "constants/plans"
const t = strings.LOGIN_STRING
// Customer logo bar taken from the marketing site (roxwrite.com), same order as
// the marquee there. The assets are pre-tinted white on transparent so they read
// on the dark brand panel.
const CLIENT_LOGOS = [
  { src: cbrtCapital, alt: "CBRT Capital" },
  { src: singlePoint, alt: "SinglePoint" },
  { src: claimFunded, alt: "Claim Funded" },
  { src: trilogy, alt: "Trilogy" },
  { src: xlerateFinancial, alt: "XLerate Financial" },
  { src: capitalHart, alt: "Capital Hart" },
  { src: boltonCapital, alt: "Bolton Capital" },
  { src: r7, alt: "R-7" },
  { src: aspenInvoiceFactoring, alt: "Aspen Invoice Factoring" },
  { src: jobe, alt: "Jobe" },
]
// Cooldown (seconds) applied to the OTP "Resend code" button.
const RESEND_COOLDOWN_SECONDS = 45

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

// Accepts only internal paths; rejects absolute/protocol-relative URLs and
// dangerous schemes to prevent open-redirect abuse.
const sanitizeTarget = (raw) => {
  if (!raw) return null
  const target = raw.trim()
  if (!target.startsWith("/") || target.startsWith("//")) return null
  if (target.includes(":")) return null
  return target
}

// Maps an auth API rejection to a user message, tolerating the three error shapes the
// backend uses: an array [{field,message}] for bean validation, { message } from the
// controllers and { Error } from the login filter, which answers before any controller.
// `unauthorized` and `invalidPayload` vary by endpoint: a 401 means bad credentials on
// /login but a dead challenge on the second step, and a rejected payload is a malformed
// email on request-otp but a weak password on set-password.
const authErrorMessage = async (err, options = {}) => {
  const {
    fallback = t.OTP_ERROR_GENERIC,
    unauthorized = t.OTP_ERROR_GENERIC,
    invalidPayload = t.OTP_EMAIL_INVALID,
  } = options
  if (!err || typeof err.status !== "number") return fallback
  // Both are raised while issuing the code, so they carry no useful body.
  if (err.status === 429) return t.TWO_FACTOR_THROTTLED
  if (err.status === 503) return t.TWO_FACTOR_DELIVERY_FAILED
  if (err.status === 401) return unauthorized
  let body
  try {
    body = await err.json()
  } catch {
    return fallback
  }
  if (Array.isArray(body)) return invalidPayload
  const message = body?.message || body?.Error
  if (message === "Code expired") return t.OTP_EXPIRED
  if (message === "Invalid code") return t.OTP_INVALID
  // Code-only sign-in refused because the account has a password and 2FA is in force.
  if (err.status === 403) return t.TWO_FACTOR_PASSWORD_REQUIRED
  if (err.status === 409) return t.CREATE_PASSWORD_EXISTS
  return fallback
}

function LoginPage(props) {
  const { onLogIn, match, history } = props
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [otpCode, setOtpCode] = useState("")
  const [otpRequested, setOtpRequested] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)
  // Proof that the password step was cleared. Only opens /login/verify-2fa, and it is
  // deliberately kept out of storage so a reload restarts from the password.
  const [challengeToken, setChallengeToken] = useState("")
  // Proof that a passwordless account verified a code and still owes us a password.
  const [setupToken, setSetupToken] = useState("")
  const [loggingIn, setLoggingIn] = useState("Login")
  const [view, setView] = useState("login")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})
  // Sticky message carried into the password form, e.g. after a code-only attempt was
  // refused because the account is under two-factor. A toast would be gone by then.
  const [notice, setNotice] = useState("")
  const [openTooltip, setOpenTooltip] = useState(false)
  const [valid, setValid] = useState(false)
  const tokenId = match?.params?.tokenId
  const confirmationToken = match?.params?.confirmationToken

  // Keep the rendered view in sync with the URL. This covers direct
  // navigation and browser back/forward (which change the pathname without
  // going through our click handlers). Within-page steps that don't change
  // the pathname (e.g. otp-email -> otp-code) are left to their own handlers.
  useEffect(() => {
    const path = history.location.pathname
    if (path === '/get-started') {
      setView("register");
    } else if (path === '/login/register-otp') {
      setView("register-otp");
    } else if (path === '/login/forgot-password') {
      setView("forgot password");
      setLoggingIn("Send email");
    } else if (path === '/login') {
      setView("login");
      setLoggingIn("Login");
      setOtpRequested(false);
      setResendCooldown(0);
      setChallengeToken("");
      setSetupToken("");
    }
  }, [history.location.pathname])

  // OTP route (/login/otp). Supports an optional ?email= deep-link that
  // pre-fills the email and jumps to step 2, WITHOUT auto-requesting a code
  // (mail scanners/prefetch would otherwise burn the throttle). The query
  // param is stripped from the URL so the email never stays in history.
  useEffect(() => {
    if (history.location.pathname !== '/login/otp') return
    const params = new URLSearchParams(history.location.search)
    const emailParam = (params.get('email') || '').trim()
    // Optional ?target= deep-link consumed by handleLogin (App.js) after verify.
    const target = sanitizeTarget(params.get('target'))
    if (target) {
      localStorage.setItem('redirectUrl', target)
      sessionStorage.setItem('deepLinkRedirect', 'true')
    }
    if (emailParam && isValidEmail(emailParam)) {
      setEmail(emailParam)
      setView("otp-code")
    } else {
      setView("otp-email")
    }
    setOtpRequested(false)
    setOtpCode("")
    if (history.location.search) {
      window.history.replaceState(null, '', '/login/otp')
    }
  }, [history.location.pathname, history.location.search])

  const redirectToLogin = useCallback(() => {
    setView("login");
    setLoggingIn("Login");
    setPassword("");
    setOtpCode("");
    setOtpRequested(false);
    setResendCooldown(0);
    setChallengeToken("");
    setSetupToken("");
    setNotice("");
    history.push("/login")
  }, [history])

  // Abandons a pending two-factor challenge or password setup and returns to the password
  // form. Stays on /login, so unlike redirectToLogin it does not push history.
  const backToPasswordLogin = () => {
    setChallengeToken("")
    setSetupToken("")
    setOtpCode("")
    setPassword("")
    setConfirmPassword("")
    setResendCooldown(0)
    setValidationErrors({})
    setLoggingIn("Login")
    setView("login")
  }

  useEffect(() => {
    if (tokenId) {
      setLoading(true)
      setView("reset password")
      setLoggingIn("Reset password")
      request(`user/check-token/${tokenId}`, 'GET').then((res) => {
        if (res.tokenValid === false) {
          Toast({
            type: "error",
            message: "Token Expired"
          })
          redirectToLogin();
        }
      }).catch(e => {
        Toast({
          type: "error",
          message: "Invalid Token"
        })
        redirectToLogin();
      }).finally(() => setLoading(false))
    }

  }, [tokenId, match, redirectToLogin])

  useEffect( () =>  {
    if (confirmationToken) {
      setLoading(true)
      setView("confirmation email")
      request(`email-confirmation`, 'POST',{validationSecurityToken:confirmationToken}).then((res) => {
        Toast({
          type: "success",
          message: "Registration completed successfully",
        })
        localStorage.setItem('redirectUrl', "/welcome-survey")
        localStorage.setItem('showWelcomeSurvey', "true")
      }).catch(e => {
        Toast({
          type: "error",
          message: "Invalid confirmation"
        })
        redirectToLogin();
      }).finally(() => setLoading(false))
    }

  }, [confirmationToken, match, redirectToLogin])

  // useCallback(() => redirectToLogin)

  // Cooldown countdown for the OTP "Resend code" button (UX only; the backend
  // silently throttles extra requests).
  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const timer = setInterval(() => {
      setResendCooldown((s) => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(timer)
  }, [resendCooldown])

  // Switch to the passwordless (email code) flow (step 1) on its own route,
  // without touching the password form state.
  const goToOtpFlow = () => {
    setView("otp-email")
    setOtpCode("")
    setOtpRequested(false)
    setValidationErrors({})
    setNotice("")
    history.push("/login/otp")
  }

  // Go back to step 1 (email) from step 2, e.g. via "Change email".
  const changeOtpEmail = () => {
    setView("otp-email")
    setOtpCode("")
    setOtpRequested(false)
    setResendCooldown(0)
  }

  // A code-only sign-in the two-factor policy will never allow (403). Hands the user over to
  // the password form with the reason pinned, instead of leaving them on a dead code screen.
  const switchToPasswordLogin = () => {
    setNotice(t.TWO_FACTOR_PASSWORD_REQUIRED)
    setOtpCode("")
    setOtpRequested(false)
    setResendCooldown(0)
    setLoggingIn("Login")
    setView("login")
    history.push("/login")
  }

  // Requests an OTP code. Used by step 1 submit, the deep-link "Send me the
  // code" button, and "Resend code". The answer is neutral (it never reveals whether the
  // email exists) except for the 403 the backend raises for accounts kept on the password
  // flow by two-factor: those are turned away here, before any code is sent.
  const requestOtpCode = () => {
    const normalizedEmail = email.trim()
    if (!isValidEmail(normalizedEmail)) {
      Toast({ type: strings.ERROR, message: t.OTP_EMAIL_INVALID })
      return
    }
    const isResend = otpRequested
    setLoading(true)
    Auth.requestOtp(normalizedEmail).then(() => {
      Toast({ type: "info", message: isResend ? t.OTP_RESENT : t.OTP_CODE_SENT })
      setOtpRequested(true)
      setView("otp-code")
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    }).catch(async (err) => {
      if (err?.status === 403) {
        switchToPasswordLogin()
        return
      }
      Toast({ type: strings.ERROR, message: await authErrorMessage(err) })
    }).finally(() => setLoading(false))
  }

  // Step 2 of OTP: verify the code. On success, reuse the exact same
  // post-login handler as the password flow (onLogIn).
  const verifyOtpCode = () => {
    const normalizedCode = otpCode.trim()
    if (!normalizedCode) {
      Toast({ type: strings.ERROR, message: t.OTP_CODE_REQUIRED })
      return
    }
    setLoading(true)
    Auth.verifyOtp(email.trim(), normalizedCode).then((res) => {
      // Passwordless account under a two-factor client: no session yet, it has to create a
      // password first. The setup token is the only thing that opens /login/set-password.
      if (res?.passwordSetupRequired) {
        setSetupToken(res.setupToken)
        setPassword("")
        setConfirmPassword("")
        setValidationErrors({})
        setView("create-password")
        setLoading(false)
        return
      }
      onLogIn()
    }).catch(async (err) => {
      // Only reachable if two-factor was switched on after the code went out: request-otp
      // already turns these accounts away.
      if (err?.status === 403) {
        setLoading(false)
        switchToPasswordLogin()
        return
      }
      Toast({ type: strings.ERROR, message: await authErrorMessage(err) })
      setLoading(false)
    })
  }

  // Second step of a two-factor login. A dead challenge cannot be recovered with another
  // code, so it sends the user back to the password form.
  const verifyTwoFactorCode = () => {
    const normalizedCode = otpCode.trim()
    if (!normalizedCode) {
      Toast({ type: strings.ERROR, message: t.OTP_CODE_REQUIRED })
      return
    }
    setLoading(true)
    Auth.verifyTwoFactor(challengeToken, normalizedCode).then(() => {
      onLogIn()
    }).catch(async (err) => {
      const challengeLost = err?.status === 401
      Toast({
        type: strings.ERROR,
        message: await authErrorMessage(err, { unauthorized: t.TWO_FACTOR_CHALLENGE_EXPIRED })
      })
      setLoading(false)
      if (challengeLost) backToPasswordLogin()
    })
  }

  const resendTwoFactorCode = () => {
    setLoading(true)
    Auth.resendTwoFactor(challengeToken).then(() => {
      Toast({ type: "info", message: t.OTP_RESENT })
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
    }).catch(async (err) => {
      const challengeLost = err?.status === 401
      Toast({
        type: strings.ERROR,
        message: await authErrorMessage(err, { unauthorized: t.TWO_FACTOR_CHALLENGE_EXPIRED })
      })
      if (challengeLost) backToPasswordLogin()
    }).finally(() => setLoading(false))
  }

  // First password of an account that got in with a login code. Succeeding here is what
  // finally hands out the session.
  const createInitialPassword = () => {
    if (validate()) return
    if (!valid) {
      Toast({ type: strings.ERROR, message: t.CREATE_PASSWORD_INVALID })
      return
    }
    setLoading(true)
    Auth.setInitialPassword(setupToken, password).then(() => {
      onLogIn()
    }).catch(async (err) => {
      Toast({
        type: strings.ERROR,
        message: await authErrorMessage(err, {
          unauthorized: t.TWO_FACTOR_CHALLENGE_EXPIRED,
          invalidPayload: t.CREATE_PASSWORD_INVALID
        })
      })
      setLoading(false)
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (view === "login") {
      // stop here if form is invalid
      setNotice("")
      setLoggingIn("Signing in…")
      if (!(email && password)) {
        setLoggingIn("Login")
        Toast({
          type: strings.ERROR,
          message: t.FORM_ERR_MSG
        })
        return
      }
      Auth.login(email, password).then((res) => {
        // Two-factor client: the password was right but the session waits for the code.
        if (res?.twoFactorRequired) {
          setLoggingIn("Login")
          setChallengeToken(res.challengeToken)
          setOtpCode("")
          setResendCooldown(RESEND_COOLDOWN_SECONDS)
          setView("two-factor")
          Toast({ type: "info", message: t.TWO_FACTOR_SENT })
          return
        }
        onLogIn()
      }).catch(async (err) => {
        setLoggingIn("Login")
        // 429 and 503 mean the password was accepted and the code could not be sent, so
        // "incorrect credentials" would be a lie. The mapper tells them apart.
        Toast({
          type: strings.ERROR,
          message: await authErrorMessage(err, { fallback: t.API_ERR_MSG, unauthorized: t.API_ERR_MSG })
        })
      })
    }
    else if (view === "otp-email") {
      requestOtpCode()
    }
    else if (view === "otp-code") {
      // Deep-link lands here without a code sent yet: first send, then verify.
      if (otpRequested) {
        verifyOtpCode()
      } else {
        requestOtpCode()
      }
    }
    else if (view === "two-factor") {
      verifyTwoFactorCode()
    }
    else if (view === "create-password") {
      createInitialPassword()
    }
    else if (view === "forgot password") {
      setLoading(true)
      request(`user/forgot-password`, 'POST', { email }).then((res) => {
        Toast({
          type: "success",
          message: res.responseMessage
        })
        redirectToLogin();
      }).finally(() => setLoading(false))
    }
    else {
      if (!validate()) {
        setLoading(true)
        const payload = { password, confirmPassword, token: tokenId }
        if (valid)
          request('user/recover-password', 'POST', payload).then((res) => {
            Toast({
              type: "success",
              message: res.message
            })
            redirectToLogin();
          }).catch((e) => {
            console.log("error", e);
          }).finally(() => setLoading(false))
      }
    }
  }

  const validate = () => {
    let isError = false
    const errors = {}

    if (password.trim().length < 8) {
      errors.password = "required minimum 8 characters"
      isError = true
    }
    if (password.trim().length > 20) {
      errors.password = "maximum 20 characters"
      isError = true
    }
    if (confirmPassword !== password) {
      errors.confirmPassword = "password does not match"
      isError = true
    }

    setValidationErrors(errors)
    return isError
  }

  const handleView = () => {
    if (view === "login") {
      history.push("/login/forgot-password")
    }
    else if (view === "forgot password") {
      redirectToLogin()
    }
  }

  // Views where the password being typed is a new one, not an existing credential.
  const isNewPasswordView = view === "reset password" || view === "create-password"

  const passwordField = (
    <div className="gs-field">
      <TypoGraph
        variant="body1"
        content={view === "reset password" ? "New password" : "Password"}
        className="gs-label"
      />
      <InputFieldDetailed
        fullWidth
        name="password"
        label=""
        type="password"
        id="password"
        autoComplete={isNewPasswordView ? "new-password" : "current-password"}
        placeholder={isNewPasswordView ? "Create a password" : ""}
        className="gs-input-field"
        value={password}
        variant="outlined"
        size="small"
        onChange={(e) => setPassword(e.target.value)}
        disabled={loading}
        error={validationErrors.password}
        onFocus={() => setOpenTooltip(true)}
        onBlur={() => setOpenTooltip(false)}
      />
    </div>
  )

  // One box per code character; the code itself stays a plain upper-case string.
  const codeField = (
    <div className="gs-field">
      <TypoGraph variant="body1" content={"Code"} className="gs-label" />
      <OtpCodeInput value={otpCode} onChange={setOtpCode} disabled={loading} />
      <div className="gs-hint">{t.OTP_CODE_HINT}</div>
    </div>
  )

  const resendCodeLink = (onResend) => (
    <div
        className="gs-link-action"
        role="button"
        onClick={() => { if (resendCooldown <= 0 && !loading) onResend() }}
        style={{ cursor: resendCooldown > 0 ? "default" : "pointer", opacity: resendCooldown > 0 ? 0.6 : 1 }}
    >
      {resendCooldown > 0 ? `${t.OTP_RESEND_IN} ${resendCooldown}s` : t.OTP_RESEND}
    </div>
  )

  const isRegisterView = view === "register" || view === "register-otp"

  // Card heading per view. The sign-in views reuse the same title/subtitle slots
  // as the register card so both sides of the flow read the same.
  const cardHeading = {
    "forgot password": { title: "Forgot password", subtitle: t.HINT },
    "otp-email": { title: "Sign in with a code", subtitle: t.OTP_EMAIL_HINT },
    "otp-code": {
      title: "Enter your code",
      subtitle: otpRequested ? t.OTP_CODE_SENT : `${t.OTP_DEEPLINK_PROMPT} ${email}`,
    },
    "reset password": { title: "Reset password", subtitle: "" },
    "two-factor": { title: t.TWO_FACTOR_TITLE, subtitle: t.TWO_FACTOR_SENT },
    "create-password": { title: t.CREATE_PASSWORD_TITLE, subtitle: t.CREATE_PASSWORD_HINT },
  }[view] || { title: "Login", subtitle: "Sign in to your ROX account." }

  return (
    <div className={clsx("login-wrapper", "gs-register")}>
      <Grid container>
        <Grid item xs={false} sm={6} md={6} lg={6} className="d-none d-sm-block">
            <div className="gs-brand">
              <img className="gs-brand-logo" src={whiteLogo} alt="ROX" />
              <div className="gs-brand-inner">
                {isRegisterView ? (
                  <>
                    <h1>Create your account.</h1>
                    <p className="gs-sub">{`Your account starts off with ${TRIAL_INCLUDED_CREDITS} credits which is typically enough to run 3+ applications.`}</p>
                  </>
                ) : (
                  <>
                    <h1>Welcome back.</h1>
                    <p className="gs-sub">Faster than Underwrite. Roxwrite.</p>
                  </>
                )}
                <figure className="gs-quote">
                  <div className="gs-stars" aria-label="5 stars">&#9733; &#9733; &#9733; &#9733; &#9733;</div>
                  <blockquote>Thank God for ROX. You saved me from onboarding a fraudulent client.</blockquote>
                  <figcaption>Hunter at Bank Shot, ROX customer</figcaption>
                </figure>
                <p className="gs-trust">Trusted by leading lenders, factors, and brokers.</p>
                <div className="gs-logo-bar">
                  {CLIENT_LOGOS.map((client) => (
                    <img key={client.alt} src={client.src} alt={client.alt} />
                  ))}
                </div>
              </div>
            </div>
        </Grid>
        {view === "register" ?
           <SelfRegister redirectToLogin={redirectToLogin} view={view} setView={setView} />
            : view === "register-otp" ?
           <RegisterOtp onLogIn={onLogIn} redirectToLogin={redirectToLogin} history={history} />
            :
            <Grid item xs={12} sm={6} md={6} lg={6} className="gs-form-col" style={{zIndex: 1}}>
              <form name="form" onSubmit={handleSubmit} className={`self-register-container gs-card${view === "selfRegisterSuccessful" || view === "confirmation email" ? " gs-card-confirm" : ""}`}>
                {    view === "selfRegisterSuccessful" ? <ConfirmationEmailPage title={strings.PLEASE_CONFIRM_EMAIl} paragraph={strings.CONFIRM_EMAIL_TEXT} redirectToLogin={redirectToLogin}/>
                 : view === "confirmation email" ?
                    <ConfirmationEmailPage title={strings.WELCOME_TO_ROXWRITE} paragraph={strings.CONFIRMATION_EMAIL_TEXT} redirectToLogin={redirectToLogin}/> :
                <>
                  <img className="gs-form-logo" src={darkLogo} alt="ROX" />

                  <div className="gs-card-head">
                    <TypoGraph variant="h4" content={cardHeading.title} className="gs-title" />
                    {cardHeading.subtitle ?
                        <TypoGraph
                            variant="body1"
                            content={cardHeading.subtitle}
                            className="gs-subtitle"
                            style={{ wordBreak: "break-word" }}
                        /> : null}
                  </div>

                  {view === "login" && notice ?
                      <div className="gs-notice" role="alert">{notice}</div> : null}

                  {view === "login" || view === "forgot password" || view === "otp-email" ?
                      <div className="gs-field">
                        <TypoGraph
                            variant="body1"
                            content={view === "login" ? "Username" : "Work email"}
                            className="gs-label"
                        />
                        <InputFieldDetailed
                            fullWidth
                            id="username"
                            label=""
                            name="username"
                            autoComplete="username"
                            placeholder={view === "login" ? "" : "jane@company.com"}
                            className="gs-input-field"
                            value={email}
                            variant="outlined"
                            size="small"
                            disabled={view === "otp-email" && loading}
                            hideValidationText
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {view === "otp-email" ?
                            <div className="gs-hint">{t.OTP_TWO_FACTOR_NOTICE}</div> : null}
                      </div> : null}

                  {view === "login" && passwordField}

                  {view === "otp-code" &&
                      <>
                        {otpRequested &&
                            <>
                              {codeField}
                              {resendCodeLink(requestOtpCode)}
                            </>}
                        <div
                            className="gs-link-action"
                            role="button"
                            onClick={changeOtpEmail}
                            style={{ cursor: "pointer" }}
                        >
                          {t.OTP_CHANGE_EMAIL}
                        </div>
                      </>}

                  {view === "two-factor" &&
                      <>
                        {codeField}
                        {resendCodeLink(resendTwoFactorCode)}
                      </>}

                  {isNewPasswordView &&
                      <PasswordValidator password={password} isValid={setValid} open={openTooltip}>
                        {passwordField}
                      </PasswordValidator>}


                  {isNewPasswordView &&
                      <div className="gs-field">
                        <TypoGraph variant="body1" content={"Confirm password"} className="gs-label" />
                        <InputFieldDetailed
                            fullWidth
                            name="confirmPassword"
                            label=""
                            type="password"
                            id="confirmPassword"
                            placeholder="Re-enter your password"
                            className="gs-input-field"
                            value={confirmPassword}
                            variant="outlined"
                            size="small"
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            error={validationErrors.confirmPassword}
                        />
                      </div>}

                  {view === "login" || view === "forgot password" ?
                      <div
                          className="gs-link-action"
                          role="button"
                          onClick={handleView}
                          style={{ cursor: "pointer" }}
                      >
                        {view === "login" ? t.FORGOT_PASSWORD : t.BACK_TO_LOGIN}
                      </div> : null}
                  {view === "otp-email" || view === "otp-code" ?
                      <div
                          className="gs-link-action"
                          role="button"
                          onClick={redirectToLogin}
                          style={{ cursor: "pointer" }}
                      >
                        {t.OTP_BACK_TO_PASSWORD}
                      </div> : null}
                  {view === "two-factor" || view === "create-password" ?
                      <div
                          className="gs-link-action"
                          role="button"
                          onClick={backToPasswordLogin}
                          style={{ cursor: "pointer" }}
                      >
                        {t.BACK_TO_LOGIN}
                      </div> : null}
                  <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      className="gs-cta mt-3"
                      type="submit"
                      disabled={loading}
                  >
                    {loading ? <CircularProgress size={25} color="inherit"/>
                      : view === "otp-email" ? t.OTP_SEND_CODE
                      : view === "otp-code" ? (otpRequested ? t.OTP_VERIFY : t.OTP_SEND_ME_CODE)
                      : view === "two-factor" ? t.OTP_VERIFY
                      : view === "create-password" ? t.CREATE_PASSWORD_CTA
                      : loggingIn}
                  </Button>
                  {process.env.NODE_ENV === "development" && window.location.hostname === "localhost" && view === "login" && process.env.REACT_APP_QUICK_LOGIN_USERNAME ?
                      <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          className="mt-2"
                          id="quick-login"
                          disabled={loading}
                          onClick={() => {
                            setEmail(process.env.REACT_APP_QUICK_LOGIN_USERNAME)
                            setPassword(process.env.REACT_APP_QUICK_LOGIN_PASSWORD || "")
                          }}
                      >
                        Quick login
                      </Button> : null}
                  {process.env.NODE_ENV === "development" && window.location.hostname === "localhost" && view === "login" && process.env.REACT_APP_QUICK_LOGIN_ADMIN_USERNAME ?
                      <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          className="mt-2"
                          id="quick-login-admin"
                          disabled={loading}
                          onClick={() => {
                            setEmail(process.env.REACT_APP_QUICK_LOGIN_ADMIN_USERNAME)
                            setPassword(process.env.REACT_APP_QUICK_LOGIN_ADMIN_PASSWORD || "")
                          }}
                      >
                        Quick login (super admin)
                      </Button> : null}
                  {/* Passwordless sign-in is available in every environment (no feature flag). */}
                  {view === "login" ?
                      <div
                          className="gs-link-action gs-link-center"
                          role="button"
                          tabIndex={0}
                          onClick={() => { if (!loading) goToOtpFlow() }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!loading) goToOtpFlow() } }}
                          style={{ cursor: loading ? "default" : "pointer", opacity: loading ? 0.6 : 1 }}
                      >
                        {t.OTP_LINK}
                      </div> : null}
                { view ==="login" ? <hr className="gs-divider" /> : null}
                { view ==="login" && !!process.env.REACT_APP_SHOW_SELF_REGISTER &&
                process.env.REACT_APP_SHOW_SELF_REGISTER !== 'false' ? <Grid container={true} justifyContent="center" spacing={2} style={{ marginTop: "1rem" }}>
                  <TypoGraph
                      variant="body1"
                      content={"Don't have an account?"}
                      className={"mr-1"}
                  />
                  <TypoGraph
                      variant="body2"
                      content={'Sign up'}
                      className={"sign-up-button"}
                      onClick={() => {
                        history.push("/get-started");
                      }}
                  />
                </Grid>: null}
                </> }
              </form>
            </Grid>
        }
      </Grid>
    </div>
  )
}

export default LoginPage
